import type { Contact } from '../services/api'
import { countsAsOutreach } from '../constants/networking'

// MWA-354 — accountability for the contacts a strategist assigned to a client.
// "Assigned" is the reachOutAt date staff set in the contact panel (labelled
// "Assigned on" since MWA-353); the LinkedIn import never sets it, so a
// client's imported network is not assigned until a coach picks from it.
//
// A contact is awaiting outreach while it is assigned and its status is not an
// outreach. 'not_comfortable' is deliberately still awaiting: it records the
// client's reluctance, which is exactly what the coach is there to push past.

/** Settings key for the admin-configurable overdue threshold, in days. */
export const ASSIGNMENT_OVERDUE_SETTING_KEY = 'networking_assignment_overdue_days'

export const DEFAULT_ASSIGNMENT_OVERDUE_DAYS = 7

/**
 * Status-filter sentinel for "assigned, not reached out". Staff-only, and
 * unlike CONTACT_STATUS_UNSET_FILTER it needs the whole contact, not just its
 * status, so it is matched by isAwaitingOutreach rather than matchesStatusFilter.
 */
export const ASSIGNED_AWAITING_FILTER = '__assigned_awaiting__'

export const ASSIGNED_AWAITING_LABEL = 'Assigned, not reached out'

/**
 * The stored setting is a string. Anything that is not a whole number of days,
 * at least 1, reads as the default rather than as 0 — a threshold of 0 would
 * flag every contact the moment it was assigned.
 */
export function parseOverdueDays(value: string | null | undefined): number {
  if (value == null || !/^\s*\d+\s*$/.test(value)) return DEFAULT_ASSIGNMENT_OVERDUE_DAYS
  const n = Number(value)
  return n >= 1 ? n : DEFAULT_ASSIGNMENT_OVERDUE_DAYS
}

function assignedDate(contact: Pick<Contact, 'reachOutAt'>): Date | null {
  if (!contact.reachOutAt) return null
  const d = new Date(contact.reachOutAt)
  return Number.isNaN(d.getTime()) ? null : d
}

export function isAssigned(contact: Pick<Contact, 'reachOutAt'>): boolean {
  return assignedDate(contact) !== null
}

export function isAwaitingOutreach(contact: Pick<Contact, 'reachOutAt' | 'status'>): boolean {
  return isAssigned(contact) && !countsAsOutreach(contact.status)
}

/**
 * Calendar days, in the viewer's zone, from the assignment to `now` — so a
 * contact assigned late yesterday is one day old this morning, not "today".
 * A future-dated assignment reads as 0 rather than a negative age.
 */
export function daysSinceAssigned(iso: string, now: Date): number {
  const assigned = new Date(iso)
  if (Number.isNaN(assigned.getTime())) return 0
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  // Rounded, not floored: a day that crosses a DST change is 23 or 25 hours.
  const days = Math.round((startOf(now) - startOf(assigned)) / 86_400_000)
  return Math.max(0, days)
}

export interface AssignmentAge {
  days: number
  overdue: boolean
}

/**
 * How long an awaiting contact has waited, or null for one that is not
 * awaiting outreach (unassigned, or already reached out). Overdue once the
 * whole-day age passes the threshold, so the age shown beside the flag always
 * agrees with it.
 */
export function assignmentAge(
  contact: Pick<Contact, 'reachOutAt' | 'status'>,
  thresholdDays: number,
  now: Date
): AssignmentAge | null {
  if (!isAwaitingOutreach(contact)) return null
  const days = daysSinceAssigned(contact.reachOutAt as string, now)
  return { days, overdue: days > thresholdDays }
}

export function formatAssignmentAge(days: number): string {
  if (days === 0) return 'Assigned today'
  return `${days} ${days === 1 ? 'day' : 'days'}, no outreach`
}

export interface AssignmentSummaryCounts {
  assigned: number
  awaiting: number
  overdue: number
  /** Age in days of the longest-waiting awaiting contact; null when none await. */
  oldestAwaitingDays: number | null
}

export function summarizeAssignments(
  contacts: Pick<Contact, 'reachOutAt' | 'status'>[],
  thresholdDays: number,
  now: Date
): AssignmentSummaryCounts {
  let assigned = 0
  let awaiting = 0
  let overdue = 0
  let oldestAwaitingDays: number | null = null
  for (const c of contacts) {
    if (!isAssigned(c)) continue
    assigned++
    const age = assignmentAge(c, thresholdDays, now)
    if (!age) continue
    awaiting++
    if (age.overdue) overdue++
    if (oldestAwaitingDays === null || age.days > oldestAwaitingDays) oldestAwaitingDays = age.days
  }
  return { assigned, awaiting, overdue, oldestAwaitingDays }
}
